import { IcoBook16, IcoBuilding16, IcoChecklist16, IcoFileSearch16, IcoRobot16, IcoUser16 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import type { ResourceContext } from '@onefootprint/request-types/percy';
import type { MentionAttrs, MentionKind } from './types';

const ICON_BY_KIND: Record<MentionKind, typeof IcoUser16> = {
  person: IcoUser16,
  business: IcoBuilding16,
  playbook: IcoBook16,
  investigation: IcoFileSearch16,
  onboarding: IcoChecklist16,
  agent_version: IcoRobot16,
  agent: IcoRobot16,
};

export const getIconForKind = (kind: MentionKind): typeof IcoUser16 => ICON_BY_KIND[kind];

export const getEntityKindFromId = (id: string): MentionKind | null => {
  if (id.startsWith('fp_bid_')) return 'business';
  if (id.startsWith('fp_id_')) return 'person';
  if (id.startsWith('pb_')) return 'playbook';
  if (id.startsWith('inv_')) return 'investigation';
  if (id.startsWith('ob_')) return 'onboarding';
  if (id.startsWith('av_')) return 'agent_version';
  if (id.startsWith('ag_')) return 'agent';
  return null;
};

export const getEntityHref = (id: string, kind: MentionKind): string | null => {
  switch (kind) {
    case 'business':
      return `/businesses/${id}`;
    case 'playbook':
      return `/playbooks/${id}`;
    case 'investigation':
      return `/investigations/${id}`;
    // The onboarding trace route is /users/<fp_id>/onboardings/<ob_id>, but we
    // only have the onboarding id here (no owning fp_id), so there's no link.
    case 'onboarding':
      return null;
    case 'agent_version':
      return `/agents/builder?versionId=${id}&mode=edit`;
    case 'agent':
      return `/agents/${id}/builder?mode=edit`;
    default:
      return `/users/${id}`;
  }
};

export const getEntityLabel = (entity: Entity): string => {
  const nameAttr = entity.data.find(d => d.identifier === 'id.first_name' || d.identifier === 'business.name');
  const lastNameAttr = entity.data.find(d => d.identifier === 'id.last_name');
  if (nameAttr && lastNameAttr) return `${nameAttr.value ?? ''} ${lastNameAttr.value ?? ''}`.trim() || entity.id;
  if (nameAttr?.value) return String(nameAttr.value);
  return entity.id;
};

// Null for kinds the picker doesn't emit today (`agent_version`, `investigation`);
// callers filter them out.
export const mentionAttrsToResourceContext = ({ id, label, kind }: MentionAttrs): ResourceContext | null => {
  switch (kind) {
    case 'person':
    case 'business':
      return { kind: 'entity', entity_kind: kind, id, name: label };
    case 'agent':
      return { kind: 'agent', id, name: label, selection: null };
    case 'playbook':
      return {
        kind: 'playbook',
        id,
        name: label,
        edit_session_id: null,
        version_id: null,
        selection: null,
        update_description: null,
      };
    default:
      return null;
  }
};
